// Toast composable — toast notifications with localStorage history
import { ref } from 'vue'
import type { ToastHistoryEntry } from '@/types'

export interface ActiveToast {
  id: number
  message: string
  type: 'default' | 'success' | 'error'
  visible: boolean
}

let toastIdCounter = 0
const activeToasts = ref<ActiveToast[]>([])
const toastHistory = ref<ToastHistoryEntry[]>([])
const hasUnread = ref(false)

function loadHistory(): ToastHistoryEntry[] {
  const raw = JSON.parse(localStorage.getItem('statuspage-toast-history') || '[]')
  const cutoff = Date.now() - 24 * 60 * 60 * 1000
  const filtered = raw.filter((toast: ToastHistoryEntry) => {
    if (!toast || !toast.timestamp) return false
    const ts = new Date(toast.timestamp).getTime()
    return Number.isFinite(ts) && ts >= cutoff
  })
  if (filtered.length !== raw.length) {
    localStorage.setItem('statuspage-toast-history', JSON.stringify(filtered.slice(-10)))
  }
  return filtered.slice(-10)
}

function checkUnread(): boolean {
  const history = loadHistory()
  if (!history.length) return false
  const lastViewed = localStorage.getItem('statuspage-toast-last-viewed')
  if (!lastViewed) return true
  const lastViewedTime = new Date(lastViewed).getTime()
  return history.some((toast) => {
    const ts = toast.timestamp ? new Date(toast.timestamp).getTime() : 0
    return ts > lastViewedTime
  })
}

export function useToasts() {
  function refreshHistory() {
    toastHistory.value = loadHistory()
    hasUnread.value = checkUnread()
  }

  function showToast(message: string, type: 'default' | 'success' | 'error' = 'default') {
    const id = ++toastIdCounter
    const toast: ActiveToast = { id, message, type, visible: false }
    activeToasts.value.push(toast)

    // Save to history
    const entry: ToastHistoryEntry = { message, type, timestamp: new Date().toISOString() }
    const history = loadHistory()
    history.push(entry)
    const trimmed = history.slice(-10)
    localStorage.setItem('statuspage-toast-history', JSON.stringify(trimmed))
    refreshHistory()

    // Trigger animation
    setTimeout(() => {
      const t = activeToasts.value.find((t) => t.id === id)
      if (t) t.visible = true
    }, 10)

    // Remove after duration
    const duration = type === 'success' ? 5000 : 2000
    setTimeout(() => {
      const t = activeToasts.value.find((t) => t.id === id)
      if (t) t.visible = false
      setTimeout(() => {
        const idx = activeToasts.value.findIndex((t) => t.id === id)
        if (idx >= 0) activeToasts.value.splice(idx, 1)
      }, 300)
    }, duration)
  }

  function markViewed() {
    localStorage.setItem('statuspage-toast-last-viewed', new Date().toISOString())
    hasUnread.value = false
  }

  // Init
  refreshHistory()

  return {
    activeToasts,
    toastHistory,
    hasUnread,
    showToast,
    refreshHistory,
    markViewed,
  }
}
